/**
 * LocationContext
 *
 * React Context for providing location information to child components
 * without prop drilling. Used by LocationWrapper and consumed by any
 * component needing to know its current spatial location.
 */

import React, { createContext, useMemo } from "react";
import { CampusLocation, LocationConfig } from "../../types/spatial";
import { getLocationConfig } from "../../config/locationConfig";

export interface LocationContextValue {
  /** Current location ID */
  location: CampusLocation;
  /** Full location configuration */
  config: LocationConfig;
  /** Whether we're inside a location (not at campus overview) */
  isInsideLocation: boolean;
}

const LocationContext = createContext<LocationContextValue | null>(null);

export interface LocationProviderProps {
  location: CampusLocation;
  children: React.ReactNode;
}

/**
 * Provider component that wraps children with location context
 */
export const LocationProvider: React.FC<LocationProviderProps> = ({
  location,
  children,
}) => {
  const value = useMemo<LocationContextValue>(
    () => ({
      location,
      config: getLocationConfig(location),
      isInsideLocation: location !== CampusLocation.CAMPUS_OVERVIEW,
    }),
    [location],
  );

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
